import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Send, Users, FileText } from 'lucide-react';
import { useLocalTaskManagement } from '../../hooks/tasks/useLocalTaskManagement'; 
import { TaskPriority, TaskFormData } from '../../types/task';
import { Department } from '../../types';
import { TimeSlotSelector } from './TimeSlotSelector';
import { SubTaskForm } from './SubTaskForm';
import { getDepartmentLabel } from '../../utils/departments';
import { useEmployees } from '../../store/hooks/useEmployees';
import { ExcelTaskProcessor } from '../tasks/ExcelTaskProcessor';
import { canUseExcelUpload } from '../../utils/auth';
import { useStore } from '../../store/useStore';
import { useTaskPermissions } from '../../hooks/tasks/useTaskPermissions';
import toast from 'react-hot-toast';

interface TaskFormProps {
  department?: Department;
  onSuccess?: () => void;
}

export const TaskForm: React.FC<TaskFormProps> = ({ department, onSuccess }) => {
  const { currentUser } = useStore();
  const store = useStore();
  const addTask = store.addTask;
  const { getDepartmentEmployees } = useEmployees();
  const { canAssignToExecutive } = useTaskPermissions();
  
  // Show Excel processor for AR/AP department users
  const showExcelProcessor = currentUser && (
    department === 'ACCOUNTS_RECEIVABLE' || department === 'ACCOUNTS_PAYABLE'
  );

  const { createTask, isLoading } = useLocalTaskManagement();
  const { getAllEmployees } = useEmployees();
  
  const [task, setTask] = useState<TaskFormData>({
    title: '',
    description: '',
    assignedTo: '',
    department: department || '',
    priority: 'MEDIUM',
    dueDate: '',
    dueTime: '',
    subtasks: []
  });

  // Get all employees for assignment
  const allEmployees = getAllEmployees();
  const departments: Department[] = ['INVENTORY', 'WAREHOUSE', 'LOGISTICS', 'SUPPLIER', 'FINANCE', 'SALES'];
  const executives = [
    { id: 'sam-id', name: 'Sam El Gendy', role: 'CEO', department: 'EXECUTIVE' },
    { id: 'fady-id', name: 'Fady Soryal', role: 'CFO', department: 'EXECUTIVE' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      toast.error('You must be logged in to create tasks');
      return;
    }

    if (!task.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    if (!task.assignedTo) {
      toast.error('Please select an assignee');
      return;
    }

    if (!task.dueDate || !task.dueTime) {
      toast.error('Please set a due date and time');
      return;
    }

    // Validate date format
    try {
      new Date(`${task.dueDate}T${task.dueTime}`);
    } catch (error) {
      toast.error('Invalid date format');
      return;
    }

    try {
      // Ensure department is set from props
      const taskData = {
        ...task,
        department: department || task.department,
        // Ensure all required fields are present
        assignedBy: currentUser.id,
        status: 'PENDING',
        comments: []
      };
      
      await createTask(taskData);
      toast.success('Task assigned successfully');
      
      if (onSuccess) {
        onSuccess();
      } else {
        setTask({
          title: '',
          description: '',
          assignedTo: '',
          department: department || '',
          priority: 'MEDIUM',
          dueDate: '',
          dueTime: '',
          subtasks: []
        });
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to assign task');
    }
  };

  // Get employees for the selected department
  const departmentEmployees = department ? getDepartmentEmployees(department) : [];

  return (
    <Card className="p-6">
      {/* Show Excel Processor for AR/AP */}
      {showExcelProcessor && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Excel Data Import</h2>
              <p className="text-sm text-gray-600">
                Upload Excel files to process {department === 'ACCOUNTS_RECEIVABLE' ? 'A/R' : 'A/P'} data
              </p>
            </div>
          </div>
          <ExcelTaskProcessor department={department} />
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Users className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">
            {department ? `Assign Task to ${getDepartmentLabel(department)}` : 'Assign New Task'}
          </h2>
          <p className="text-sm text-gray-600">Create and assign tasks to team members</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <input
          type="text"
          value={task.title}
          onChange={(e) => setTask(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Task title"
          className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />

        <textarea
          value={task.description}
          onChange={(e) => setTask(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Task description"
          className="w-full px-3 py-2 border rounded-md h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />

        <div className="grid grid-cols-2 gap-4">
          {/* Assignee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign To
            </label>
            <select
              value={task.assignedTo}
              onChange={(e) => setTask(prev => ({ ...prev, assignedTo: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select assignee</option>
              {/* Show executives if user can assign to them */}
              {currentUser && canAssignToExecutive(currentUser.role) && (
                <optgroup label="Executives">
                  {executives.map((exec) => (
                    <option key={exec.id} value={exec.id}>
                      {exec.name} ({exec.role})
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Department Members">
                {departmentEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} ({getDepartmentLabel(emp.department)})
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={task.priority}
              onChange={(e) => setTask(prev => ({ ...prev, priority: e.target.value as TaskPriority }))}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="LOW">Low Priority</option>
              <option value="MEDIUM">Medium Priority</option>
              <option value="HIGH">High Priority</option>
              <option value="URGENT">Urgent</option>
            </select>
          </div>
        </div>

        <TimeSlotSelector
          selectedDate={task.dueDate}
          selectedTime={task.dueTime}
          onDateChange={(date) => setTask(prev => ({ ...prev, dueDate: date }))}
          onTimeChange={(time) => setTask(prev => ({ ...prev, dueTime: time }))}
        />

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700">Subtasks</h3>
          {task.subtasks.map((subtask, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">{subtask.title}</p>
                {subtask.hasDueDate && (
                  <p className="text-sm text-gray-600">
                    Due: {subtask.dueDate} {subtask.dueTime}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTask(prev => ({
                    ...prev,
                    subtasks: prev.subtasks.filter((_, i) => i !== index)
                  }));
                }}
                className="text-red-600 hover:bg-red-50"
              >
                Remove
              </Button>
            </div>
          ))}
          <SubTaskForm onAdd={(subtask) => {
            setTask(prev => ({
              ...prev,
              subtasks: [...prev.subtasks, subtask]
            }));
            toast.success('Subtask added');
          }} />
        </div>

        <Button
          type="submit"
          variant="primary"
          icon={<Send className="w-4 h-4" />}
          className="w-full"
          loading={isLoading}
        >
          Assign Task
        </Button>
      </form>
    </Card>
  );
};